import { useEffect, useState } from 'react'
import type { components } from './client'

export function arrayBufferToBase64(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++)
    binary += String.fromCharCode(bytes[i])
  return window.btoa(binary)
}

export function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(id)
  }, [value, delay])
  return debounced
}

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window
  return {
    width,
    height,
  }
}

export function usePersistentState<T>(
  key: string,
  initialValue: T,
  onLoad: (val: T) => void,
  validate?: (val: T) => boolean,
): [T, (value: T) => void] {
  // Initialise synchronously from localStorage so the value (e.g. the user
  // session) is available on the very first render — before router loaders run.
  const [state, setInternalState] = useState<T>(() => {
    try {
      const value = localStorage.getItem(key)
      if (value) {
        const parsed = JSON.parse(value) as T
        if (!validate || validate(parsed)) {
          onLoad(parsed)
          return parsed
        }
        localStorage.removeItem(key)
        onLoad(initialValue)
      }
    } catch {
      /* ignore malformed storage */
    }
    return initialValue
  })

  const setState = (value: T) => {
    localStorage.setItem(key, JSON.stringify(value))
    setInternalState(value)
    onLoad(value)
  }

  return [state, setState]
}

export function hasCoordinates(coords: components['schemas']['Location']) {
  return coords.latitude !== 0 && coords.longitude !== 0
}

export function useWindowDimensions() {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions(),
  )

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions())
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return windowDimensions
}

export function isGeocoded(loc: components['schemas']['Location']) {
  return loc.latitude !== 0 && loc.longitude !== 0
}

/**
 * Pretty-print bytes into human-readable format
 * @param {number} bytes - The number of bytes
 * @param {number} decimals - Decimal places (default: 2)
 * @returns {string} - Formatted string (e.g. "1.23 MB")
 */
export function prettyBytes(bytes: number, decimals = 2) {
  if (bytes === 0) return '0 B'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

export function prettyTimestamp(timestampMs: number): string {
  return `${prettyDate(timestampMs)} - ${prettyTime(timestampMs)}`
}

export function prettyDate(s: string | number): string {
  const date = new Date(s)
  const locale = 'default'

  const day = date.toLocaleString(locale, { weekday: 'long' })
  const dayNumber = date.getDate()
  const month = date.toLocaleString(locale, { month: 'long' })
  const year = date.toLocaleString(locale, { year: 'numeric' })

  return `${day} ${dayNumber} ${month}, ${year}`
}

/** Converts a display name to a URL-safe slug. Preserves leading digits intact. */
export function slugify(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

/** Builds a readable entity URL slug from id + name, e.g. "1-le-kouttab". */
export function entitySlug(id: number, name: string): string {
  return `${id}-${slugify(name)}`
}

/** Extracts the numeric entity id from a slug like "1-le-kouttab". */
export function entityIdFromSlug(slug: string): number {
  return parseInt(slug, 10)
}

export function prettyTime(s: string | number): string {
  return new Date(s).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export const durationBetween = (
  start_date: string,
  end_date: string | undefined,
) => {
  if (!end_date) return 'Durée non spécifiée'

  try {
    const start = new Date(start_date)
    const end = new Date(end_date)
    const durationMs = end.getTime() - start.getTime()

    const hours = Math.floor(durationMs / (1000 * 60 * 60))
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60))

    if (hours > 24) {
      const days = Math.floor(hours / 24)
      return `${days} jour${days > 1 ? 's' : ''}`
    }

    return `${hours}h${minutes > 0 ? ` ${minutes}min` : ''}`
  } catch (e) {
    return 'Durée non spécifiée'
  }
}

export const truncateString = (text: string, maxLength: number = 80) => {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength).trim() + '...'
}

/**
 * Whether a date looks bogus — unparseable or at/near the Unix epoch
 * (e.g. "jeudi 1 janvier 1970"), which usually means a scraper failed to
 * extract the real date. Admins should be warned rather than miss it.
 */
export function isLikelyInvalidDate(s: string | number | undefined): boolean {
  if (s === undefined || s === '') return true
  const t = new Date(s).getTime()
  if (isNaN(t)) return true
  // Anything before 1990 is almost certainly a parsing failure for this app.
  return new Date(t).getFullYear() < 1990
}

/** Short, human date — e.g. "ven. 20 juin". */
export function shortDate(s: string | number): string {
  return new Date(s).toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  })
}

/** Short time — e.g. "21:42". */
export function shortTime(s: string | number): string {
  return new Date(s).toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Split a free-form address into a venue / city pair (best effort).
 * The backend stores a single address string, so we use the last
 * comma-separated chunk as the city.
 */
export function splitAddress(address: string): { venue: string; city: string } {
  const parts = address
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean)
  if (parts.length <= 1) {
    return { venue: address, city: '' }
  }
  return { venue: parts.slice(0, -1).join(', '), city: parts[parts.length - 1] }
}

/**
 * Returns the URL only if it is safe to use as an `<a href>` — i.e. http/https
 * (or a same-page anchor / relative path). Returns undefined otherwise, which
 * causes React to omit the attribute entirely. This blocks `javascript:`,
 * `data:`, and similar schemes coming from scraped content.
 */
export function safeHref(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined
  const trimmed = raw.trim()
  if (trimmed === '') return undefined
  // Same-page or relative URLs are fine — they can't carry a dangerous scheme.
  if (
    trimmed.startsWith('/') ||
    trimmed.startsWith('#') ||
    trimmed.startsWith('?')
  ) {
    return trimmed
  }
  try {
    const u = new URL(trimmed)
    if (
      u.protocol === 'http:' ||
      u.protocol === 'https:' ||
      u.protocol === 'mailto:'
    ) {
      return trimmed
    }
  } catch {
    /* fall through */
  }
  return undefined
}

/**
 * Whether the browser can currently create a WebGL context. Returns false
 * when WebGL is unavailable *or* when the browser has blocked further contexts
 * after a prior context loss ("Web page caused context loss and was blocked").
 * Callers should show a non-WebGL fallback instead of instantiating maplibre.
 */
export function isWebglAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas')
    const gl =
      canvas.getContext('webgl2') ||
      canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl')
    return !!gl
  } catch {
    return false
  }
}

export function stripMarkdown(md: string): string {
  return md
    .replace(/#{1,6}\s+/g, '')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(\*|_)(.*?)\1/g, '$2')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*[-*>]\s+/gm, '')
    .replace(/\n+/g, ' ')
    .trim()
}
